import * as React from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { Alert, Box, Divider, Tab, Tabs, Typography } from '@mui/material'
import theme from '../theme'

const codeStyle = {
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
    color: theme.palette.mode === 'dark' ? '#d4d4d4' : '#333',
    padding: '12px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#ddd'}`,
}

const sectionStyle = { mb: 3 }
const stepTitle = { variant: "subtitle1", fontWeight: "bold", gutterBottom: true }

function TabPanel({ children, value, index }) {
    return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null
}

// ======================== Aba: Visao Geral ========================

function TabOverview() {
    return (
        <>
            <Box sx={sectionStyle}>
                <Typography variant="h6" gutterBottom>O que sao Webhooks no Horus?</Typography>
                <Typography variant="body2" paragraph>
                    Webhooks permitem que sistemas externos (Zabbix, Wazuh) enviem alarmes para o Horus
                    em <b>tempo real</b>, sem depender do ciclo de polling de 60 segundos. Quando um problema
                    ou alerta e detectado, o sistema externo faz uma requisicao HTTP POST para o Horus
                    com os dados do alarme.
                </Typography>
                <Typography variant="body2" paragraph>
                    Cada webhook usa um <b>token de autenticacao</b> vinculado a uma empresa e a um source
                    (Zabbix ou Wazuh). O token garante que apenas sistemas autorizados possam enviar alarmes.
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography variant="h6" gutterBottom>Como funciona?</Typography>
                <Typography variant="body2" component="div">
                    <ol style={{ paddingLeft: '20px', lineHeight: 2 }}>
                        <li><b>Criar token</b> — Nesta tela, clique em <b>+</b> para criar um token. Selecione a empresa e o source (Zabbix ou Wazuh).</li>
                        <li><b>Copiar URL</b> — Apos criar, clique no botao de copiar na coluna "Acoes" para obter a URL completa do webhook (com token).</li>
                        <li><b>Configurar sistema externo</b> — Use a URL no Zabbix (Media Type webhook) ou no Wazuh (active response / integration) para enviar alarmes.</li>
                        <li><b>Alarmes em tempo real</b> — Os alarmes aparecem instantaneamente no dashboard do Horus, sem esperar o proximo ciclo de coleta.</li>
                    </ol>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography variant="h6" gutterBottom>Endpoints disponiveis</Typography>
                <Box sx={codeStyle}>
{`POST /api/v1/webhooks/zabbix?token=SEU_TOKEN
  → Recebe alarmes do Zabbix (problem + recovery)

POST /api/v1/webhooks/wazuh?token=SEU_TOKEN
  → Recebe alertas de seguranca do Wazuh`}
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography variant="h6" gutterBottom>Mapeamento de Severidade</Typography>

                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Zabbix</Typography>
                <Box sx={codeStyle}>
{`Zabbix 0 (Not classified) → Descartado
Zabbix 1 (Information)    → Horus 1 (Info)
Zabbix 2 (Warning)        → Horus 2 (Aviso)
Zabbix 3 (Average)        → Horus 3 (Media)
Zabbix 4 (High)           → Horus 4 (Alta)
Zabbix 5 (Disaster)       → Horus 5 (Desastre)`}
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Wazuh</Typography>
                <Box sx={codeStyle}>
{`Wazuh 0-3   → Descartado (muito baixo)
Wazuh 4-6   → Horus 1 (Info)
Wazuh 7-9   → Horus 2 (Aviso)
Wazuh 10-11 → Horus 3 (Media)
Wazuh 12-13 → Horus 4 (Alta)
Wazuh 14-16 → Horus 5 (Desastre)`}
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography variant="h6" gutterBottom>Diferenca: Webhook vs Polling</Typography>
                <Box sx={codeStyle}>
{`┌────────────────────┬──────────────────────────────────────────┐
│ Webhook            │ Polling (API Zabbix / ES Wazuh)          │
├────────────────────┼──────────────────────────────────────────┤
│ Tempo real         │ A cada 60 segundos                       │
│ Push (sistema      │ Pull (Horus busca)                       │
│  externo envia)    │                                          │
│ Requer config no   │ So precisa cadastrar URL/credenciais     │
│  sistema externo   │  no Horus                                │
│ Complementar ao    │ Metodo principal de coleta               │
│  polling           │                                          │
└────────────────────┴──────────────────────────────────────────┘`}
                </Box>
                <Alert severity="info" sx={{ mt: 2 }}>
                    Webhooks e polling podem funcionar juntos sem duplicar alarmes.
                    O Horus usa um ID deterministico que garante deduplicacao automatica.
                </Alert>
            </Box>
        </>
    )
}

// ======================== Aba: Zabbix ========================

const ZABBIX_SCRIPT = `var params = JSON.parse(value);

var req = new HttpRequest();
req.addHeader('Content-Type: application/json');

var payload = JSON.stringify({
    event_id: params.event_id,
    host_name: params.host_name,
    host_ip: params.host_ip,
    host_id: params.host_id,
    trigger_id: params.trigger_id,
    trigger_name: params.trigger_name,
    event_severity: params.event_severity,
    event_value: params.event_value,
    event_date: params.event_date,
    event_time: params.event_time,
    item_id: params.item_id,
    tags: params.tags,
    recovery_date: params.recovery_date,
    recovery_time: params.recovery_time
});

var url = params.horus_url + '?token=' + params.horus_token;
var resp = req.post(url, payload);

return resp;`

const ZABBIX_SCRIPT_LEGACY = `var params = JSON.parse(value);

var req = new CurlHttpRequest();
req.AddHeader('Content-Type: application/json');

var payload = JSON.stringify({
    event_id: params.event_id,
    host_name: params.host_name,
    host_ip: params.host_ip,
    host_id: params.host_id,
    trigger_id: params.trigger_id,
    trigger_name: params.trigger_name,
    event_severity: params.event_severity,
    event_value: params.event_value,
    event_date: params.event_date,
    event_time: params.event_time,
    item_id: params.item_id,
    tags: params.tags,
    recovery_date: params.recovery_date,
    recovery_time: params.recovery_time
});

var url = params.horus_url + '?token=' + params.horus_token;
var resp = req.Post(url, payload);

return resp;`

const ZABBIX_PARAMS = `horus_url        = <URL do webhook copiada SEM o ?token=...>
horus_token      = <Token gerado pelo Horus>
event_id         = {EVENT.ID}
host_name        = {HOST.NAME}
host_ip          = {HOST.IP}
host_id          = {HOST.ID}
trigger_id       = {TRIGGER.ID}
trigger_name     = {TRIGGER.NAME}
event_severity   = {EVENT.NSEVERITY}
event_value      = {EVENT.VALUE}
event_date       = {EVENT.DATE}
event_time       = {EVENT.TIME}
item_id          = {ITEM.ID}
tags             = {EVENT.TAGS}
recovery_date    = {EVENT.RECOVERY.DATE}
recovery_time    = {EVENT.RECOVERY.TIME}`

function TabZabbix() {
    return (
        <>
            <Alert severity="info" sx={{ mb: 3 }}>
                Antes de comecar, crie um token com source <b>"Zabbix"</b> para a empresa desejada
                nesta tela e copie a URL do webhook.
            </Alert>

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Passo 1 — Criar Media Type no Zabbix</Typography>
                <Typography variant="body2" gutterBottom>
                    Acesse <b>Administration &gt; Media types &gt; Create media type</b>
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                    <li>Nome: <b>Horus Webhook</b></li>
                    <li>Type: <b>Webhook</b></li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Passo 2 — Adicionar parametros</Typography>
                <Typography variant="body2" gutterBottom>
                    Na aba <b>Parameters</b> do Media Type, adicione os seguintes parametros.
                    Substitua os valores de <code>horus_url</code> e <code>horus_token</code>
                    pelos dados copiados do Horus:
                </Typography>
                <Box sx={codeStyle}>{ZABBIX_PARAMS}</Box>
                <Alert severity="warning" sx={{ mt: 2 }}>
                    <b>horus_url</b> deve conter apenas a URL base, sem o <code>?token=...</code>.
                    Exemplo: <code>https://horus.empresa.com/api/v1/webhooks/zabbix</code>
                </Alert>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Passo 3 — Colar o Script</Typography>
                <Typography variant="body2" gutterBottom>
                    No campo <b>Script</b> do Media Type, cole o codigo JavaScript correspondente a versao do seu Zabbix:
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <b>Zabbix 5.4+</b> (incluindo 6.x e 7.x) usa <code>HttpRequest</code> com metodos em camelCase.<br />
                    <b>Zabbix 5.0–5.2</b> usa <code>CurlHttpRequest</code> com metodos em PascalCase.
                    Se nao tem certeza da versao, tente o script 5.4+ primeiro.
                </Alert>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Zabbix 5.4+ (incluindo 6.x e 7.x)</Typography>
                <Box sx={codeStyle}>{ZABBIX_SCRIPT}</Box>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Zabbix 5.0–5.2</Typography>
                <Box sx={codeStyle}>{ZABBIX_SCRIPT_LEGACY}</Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Passo 4 — Criar Action (Trigger Action)</Typography>
                <Typography variant="body2" gutterBottom>
                    Acesse <b>Configuration &gt; Actions &gt; Trigger actions &gt; Create action</b>
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Nome: <b>Horus - Enviar alarme</b></li>
                    <li><b>Conditions</b>: configure conforme necessidade (ex: Trigger severity &gt;= Warning)</li>
                    <li><b>Operations</b>: Add &gt; Send to Users &gt; selecione um usuario &gt; Send only to: <b>Horus Webhook</b></li>
                    <li><b>Recovery operations</b>: Add &gt; Send to Users &gt; mesmo usuario &gt; Send only to: <b>Horus Webhook</b></li>
                </Typography>
                <Alert severity="info" sx={{ mt: 1 }}>
                    <b>Recovery operations</b> e essencial! Sem ele, o Horus nao sabera quando um problema
                    foi resolvido no Zabbix, e o alarme ficara ativo indefinidamente.
                </Alert>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Passo 5 — Atribuir Media ao Usuario</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Acesse <b>Administration &gt; Users</b></li>
                    <li>Selecione o usuario usado na Action (ex: Admin)</li>
                    <li>Aba <b>Media</b> &gt; Add</li>
                    <li>Type: <b>Horus Webhook</b></li>
                    <li>Send to: <b>horus</b> (pode ser qualquer texto)</li>
                    <li>Marque todas as severidades desejadas</li>
                    <li>Clique em <b>Add</b> e depois <b>Update</b></li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Passo 6 — Testar</Typography>
                <Typography variant="body2" gutterBottom>
                    No Zabbix, va em <b>Administration &gt; Media types</b>, localize "Horus Webhook"
                    e clique em <b>Test</b>. Preencha os parametros com valores de exemplo e execute.
                </Typography>
                <Typography variant="body2" gutterBottom>
                    Voce tambem pode testar via <b>curl</b>:
                </Typography>
                <Box sx={codeStyle}>
{`curl -X POST "https://SEU_HORUS/api/v1/webhooks/zabbix?token=SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "event_id": "99999",
    "host_name": "Servidor Teste",
    "host_ip": "192.168.1.1",
    "host_id": "1",
    "trigger_id": "1",
    "trigger_name": "Teste de webhook Zabbix",
    "event_severity": "3",
    "event_value": "1",
    "event_date": "2025.01.15",
    "event_time": "14:30:00"
  }'`}
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Se retornar <code>{`{"status":"ok","action":"problem"}`}</code>, o alarme aparecera no dashboard.
                </Typography>
            </Box>
        </>
    )
}

// ======================== Aba: Wazuh ========================

const WAZUH_INTEGRATION_CONFIG = `<integration>
  <name>custom-horus</name>
  <hook_url>https://SEU_HORUS/api/v1/webhooks/wazuh?token=SEU_TOKEN</hook_url>
  <level>4</level>
  <alert_format>json</alert_format>
</integration>`

const WAZUH_INTEGRATION_SCRIPT = `#!/usr/bin/env python3
# /var/ossec/integrations/custom-horus
import sys
import json
import requests

def main():
    alert_file = open(sys.argv[1])
    hook_url = sys.argv[3]

    alert_json = json.loads(alert_file.read())
    alert_file.close()

    payload = {
        "timestamp": alert_json.get("timestamp", ""),
        "rule": {
            "level": alert_json.get("rule", {}).get("level", 0),
            "description": alert_json.get("rule", {}).get("description", ""),
            "id": alert_json.get("rule", {}).get("id", "0"),
            "groups": alert_json.get("rule", {}).get("groups", [])
        },
        "agent": {
            "id": alert_json.get("agent", {}).get("id", "0"),
            "name": alert_json.get("agent", {}).get("name", ""),
            "ip": alert_json.get("agent", {}).get("ip", "")
        },
        "full_log": alert_json.get("full_log", ""),
        "data": alert_json.get("data")
    }

    headers = {"Content-Type": "application/json"}
    response = requests.post(hook_url, json=payload, headers=headers)
    sys.exit(0)

if __name__ == "__main__":
    main()`

function TabWazuh() {
    return (
        <>
            <Alert severity="info" sx={{ mb: 3 }}>
                Antes de comecar, crie um token com source <b>"Wazuh"</b> para a empresa desejada
                nesta tela e copie a URL do webhook.
            </Alert>

            <Box sx={sectionStyle}>
                <Typography variant="h6" gutterBottom>Comportamento dos alarmes Wazuh</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Alarmes Wazuh <b>nao tem recovery automatico</b> (diferente do Zabbix)</li>
                    <li>Ficam ativos ate <b>resolucao manual</b> no dashboard ou ate o <b>auto-resolve por inatividade</b></li>
                    <li>Se o mesmo alerta (mesma regra + agente) chegar novamente, o <b>contador de ocorrencias</b> e incrementado (sem duplicar)</li>
                    <li>Alertas com <b>rule.level &lt; 4</b> sao descartados automaticamente</li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Opcao A — Integracao nativa do Wazuh (ossec.conf)</Typography>
                <Typography variant="body2" gutterBottom>
                    Edite o arquivo <code>/var/ossec/etc/ossec.conf</code> do Wazuh Manager e adicione
                    dentro do bloco <code>&lt;ossec_config&gt;</code>:
                </Typography>
                <Box sx={codeStyle}>{WAZUH_INTEGRATION_CONFIG}</Box>
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Substitua <code>SEU_HORUS</code> e <code>SEU_TOKEN</code> pelos valores reais.
                    O <code>&lt;level&gt;4&lt;/level&gt;</code> significa que apenas alertas com nivel &gt;= 4 serao enviados.
                </Alert>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Script de integracao</Typography>
                <Typography variant="body2" gutterBottom>
                    Crie o arquivo <code>/var/ossec/integrations/custom-horus</code> com o seguinte conteudo
                    e de permissao de execucao (<code>chmod 750</code>, owner <code>root:wazuh</code>):
                </Typography>
                <Box sx={codeStyle}>{WAZUH_INTEGRATION_SCRIPT}</Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Reiniciar o Wazuh Manager</Typography>
                <Box sx={codeStyle}>
{`systemctl restart wazuh-manager`}
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Opcao B — Testar via curl</Typography>
                <Typography variant="body2" gutterBottom>
                    Para validar rapidamente que o webhook esta funcionando:
                </Typography>
                <Box sx={codeStyle}>
{`curl -X POST "https://SEU_HORUS/api/v1/webhooks/wazuh?token=SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "timestamp": "2025-01-15T14:30:00.000Z",
    "rule": {
      "level": 10,
      "description": "Teste de integracao Wazuh",
      "id": "5501",
      "groups": ["syslog", "authentication_failed"]
    },
    "agent": {
      "id": "001",
      "name": "servidor-teste",
      "ip": "192.168.1.100"
    },
    "full_log": "Jan 15 14:30:00 servidor sshd: Failed password for root"
  }'`}
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    Se retornar <code>{`{"status":"ok","action":"new"}`}</code>, o alarme aparecera no dashboard
                    com severidade 3 (Media) pois rule.level 10 mapeia para severidade 3.
                </Typography>
            </Box>
        </>
    )
}

// ======================== Aba: Solucao de Problemas ========================

function TabTroubleshooting() {
    return (
        <>
            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Token invalido / 401 Unauthorized</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Verifique se o token esta correto e ativo nesta tela</li>
                    <li>Verifique se o <b>source</b> do token corresponde ao endpoint usado (token Zabbix so funciona em <code>/webhooks/zabbix</code>)</li>
                    <li>Token desativado nao pode ser reativado — crie um novo</li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Alarme nao aparece no dashboard</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Verifique se a <b>severidade</b> do alarme e &gt;= 1 (Zabbix) ou rule.level &gt;= 4 (Wazuh). Severidades mais baixas sao descartadas</li>
                    <li>Verifique o retorno da API — <code>{`"action":"discarded"`}</code> indica que o alarme foi ignorado por severidade baixa</li>
                    <li>Confira se a empresa do token esta correta</li>
                    <li>No Zabbix, verifique se <code>event_value</code> e <b>"1"</b> (problem). Valor <b>"0"</b> e recovery e remove alarmes</li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Alarme Wazuh nao some apos resolver</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Alarmes Wazuh nao tem recovery automatico como o Zabbix</li>
                    <li>Use o botao de <b>resolver</b> no dashboard (tela SOC) para remover manualmente</li>
                    <li>Alarmes Wazuh sem repeticao sao auto-resolvidos apos o <b>resolve_timeout</b> configurado (padrao: 30 minutos)</li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Alarmes duplicados</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>O Horus gera IDs deterministicos para cada combinacao de empresa + regra + agente</li>
                    <li>Se o mesmo alarme chegar via webhook E via polling, ele <b>nao duplica</b> — o sistema incrementa o contador de ocorrencias</li>
                    <li>Para Zabbix, o <code>event_id</code> original e usado diretamente como identificador</li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>ReferenceError: identifier 'HttpRequest' undefined</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Esse erro ocorre em versoes do <b>Zabbix 5.0–5.2</b>, que usam <code>CurlHttpRequest</code> em vez de <code>HttpRequest</code></li>
                    <li>Use o script alternativo para <b>Zabbix 5.0–5.2</b> disponivel na aba "Configurar Zabbix", Passo 3</li>
                </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={sectionStyle}>
                <Typography {...stepTitle}>Erro de SSL / conexao</Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, lineHeight: 2 }}>
                    <li>Se o Horus usa HTTPS com certificado auto-assinado, configure o Zabbix para ignorar SSL ou adicione o certificado ao trust store</li>
                    <li>No Wazuh, adicione <code>verify=False</code> no script de integracao (nao recomendado em producao)</li>
                    <li>Verifique se o firewall permite conexoes do Zabbix/Wazuh para o servidor Horus na porta correta</li>
                </Typography>
            </Box>
        </>
    )
}

// ======================== Componente principal ========================

export default function GuiaWebhooks(props) {
    const [tab, setTab] = React.useState(0)

    return (
        <Dialog open={props.open} fullWidth={true} maxWidth='md' scroll="paper">
            <DialogTitle>Guia de Webhooks — Horus</DialogTitle>
            <DialogContent dividers>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label="Visao Geral" />
                    <Tab label="Configurar Zabbix" />
                    <Tab label="Configurar Wazuh" />
                    <Tab label="Solucao de Problemas" />
                </Tabs>

                <TabPanel value={tab} index={0}><TabOverview /></TabPanel>
                <TabPanel value={tab} index={1}><TabZabbix /></TabPanel>
                <TabPanel value={tab} index={2}><TabWazuh /></TabPanel>
                <TabPanel value={tab} index={3}><TabTroubleshooting /></TabPanel>
            </DialogContent>
            <DialogActions>
                <Button sx={{ color: theme.palette.text.primary }} onClick={props.handlerClose}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
